/**
 * The short-lived token the voice service verifies — minted by the BFF from the
 * app's own session, exactly the chat lane's pattern (chatv1/auth.ts
 * refreshChatv1Token), for a second backend.
 *
 * Owner's ruling for the voice API (2026-09-05): the App talks to it DIRECTLY; the
 * BFF never proxies audio. So the BFF's only part is this one same-origin mint, and
 * the audio POST carries the token as a bearer. Held in memory only — a page load
 * costs one round trip, and nothing here survives the tab.
 *
 * Contract (avengents-voice-service/docs/frontend-mic-button.md §1):
 *   GET /auth/voice_token  →  200 {token, expires_at}   401 not signed in   503 voice_unconfigured
 */
let cached: { token: string; expiresAt: number } | null = null

/** Refresh this many seconds before the server's expiry, so a clip that starts
 *  uploading at the last second does not land on a token that died in transit. */
const REFRESH_AHEAD_S = 60

async function refreshVoiceToken(): Promise<string | null> {
  try {
    const res = await fetch('/auth/voice_token', { credentials: 'include', cache: 'no-store' })
    if (!res.ok) {
      cached = null
      return null
    }
    const body = (await res.json()) as { token?: string; expires_at?: number }
    if (!body.token) {
      cached = null
      return null
    }
    cached = { token: body.token, expiresAt: Number(body.expires_at ?? 0) }
    return body.token
  } catch {
    cached = null
    return null
  }
}

/**
 * A usable token, or null when the session cannot mint one (signed out, or the
 * voice lane is not configured on this environment). `force` throws the cached
 * one away first — the transcribe call does that on a 401, once.
 */
export async function ensureVoiceToken(force = false): Promise<string | null> {
  if (!force && cached && cached.expiresAt - Date.now() / 1000 > REFRESH_AHEAD_S) {
    return cached.token
  }
  return refreshVoiceToken()
}

export function __resetVoiceTokenForTest(): void {
  cached = null
}
